/** Everything the demo needs from one CPU depth frame, format already resolved. */
export interface DepthFrame {
  width: number
  height: number
  /** Raw sensor values; multiply by `rawValueToMeters` for distance. 0 = unknown. */
  raw: Uint16Array | Float32Array
  rawValueToMeters: number
  /** Maps normalised view coords to normalised depth-buffer coords. */
  uvTransform: Float32Array
  /** The view the depth was captured for — needed to unproject into world space. */
  view: XRView
  format: string
}

export type DepthReadError = 'unsupported' | 'gpu-usage' | 'no-pose' | 'no-depth' | 'bad-format'

export type DepthReadResult =
  { ok: true; frame: DepthFrame } | { ok: false; reason: DepthReadError }

export function readDepth(
  frame: XRFrame,
  refSpace: XRReferenceSpace,
  session: XRSession
): DepthReadResult {
  if (session.depthUsage === 'gpu-optimized') return { ok: false, reason: 'gpu-usage' }
  if (typeof frame.getDepthInformation !== 'function')
    return { ok: false, reason: 'unsupported' }

  const pose = frame.getViewerPose(refSpace)
  const view = pose?.views[0]
  if (!view) return { ok: false, reason: 'no-pose' }

  const info = frame.getDepthInformation(view)
  if (!info) return { ok: false, reason: 'no-depth' }

  const pixels = info.width * info.height
  let raw: Uint16Array | Float32Array
  if (info.data.byteLength === pixels * 2) raw = new Uint16Array(info.data)
  else if (info.data.byteLength === pixels * 4) raw = new Float32Array(info.data)
  else return { ok: false, reason: 'bad-format' }

  return {
    ok: true,
    frame: {
      width: info.width,
      height: info.height,
      raw,
      rawValueToMeters: info.rawValueToMeters,
      uvTransform: info.normDepthBufferFromNormView.matrix,
      view,
      format: session.depthDataFormat ?? (raw instanceof Uint16Array ? 'uint16' : 'float32'),
    },
  }
}

/** The part of a depth frame the sampler needs. Narrow so it can be tested. */
export type DepthBuffer = Pick<
  DepthFrame,
  'width' | 'height' | 'raw' | 'rawValueToMeters' | 'uvTransform'
>

/**
 * Depth at a *normalised view coordinate* — origin top-left, v growing DOWN.
 *
 * Indexing deliberately mirrors Chromium's
 * `XRCPUDepthInformation::getDepthInMeters`: scale by width/height (not
 * width-1), truncate (not round), clamp, no Y flip. Verified against the
 * browser's own sampler on device, which agreed to 0 mm.
 */
export function depthAtView(depth: DepthBuffer, u: number, v: number): number {
  const m = depth.uvTransform
  const du = m[0]! * u + m[4]! * v + m[12]!
  const dv = m[1]! * u + m[5]! * v + m[13]!

  const column = Math.min(depth.width - 1, Math.max(0, Math.trunc(du * depth.width)))
  const row = Math.min(depth.height - 1, Math.max(0, Math.trunc(dv * depth.height)))
  return depth.raw[row * depth.width + column]! * depth.rawValueToMeters
}

export function describeDepthError(reason: DepthReadError): string {
  switch (reason) {
    case 'unsupported':
      return 'No getDepthInformation — depth sensing was not granted.'
    case 'gpu-usage':
      return 'Session granted gpu-optimized depth; the CPU buffer is unavailable.'
    case 'no-pose':
      return 'No viewer pose yet — tracking is still initialising.'
    case 'no-depth':
      return 'Depth not ready. Move the phone: ARCore needs parallax to bootstrap.'
    case 'bad-format':
      return 'Unexpected depth buffer size — neither uint16 nor float32.'
  }
}
